import { useEffect, useState } from "react";
import Select from "react-select";
import { MaterialReactTable } from "material-react-table";
import api from "../Axios/api";
import Loader from "../common/Loader";
import { FileUp } from "lucide-react";
import * as XLSX from "xlsx";
const DEFAULT_FACTORY_ID = "666c0f5ebeca5ab2c65b2c85";

const factoryOptions = [
    {
        value: "666c0f5ebeca5ab2c65b2c85",
        label: "PolyBond Insulation",
    },
    {
        value: "666c0f5ebeca5ab2c65b2c86",
        label: "PolyBond Project",
    },
    {
        value: "666c0f5ebeca5ab2c65b2c87",
        label: "PolyBond RockFibre",
    },
];

const allCategoryOption = {
    value: "",
    label: "All Categories",
};

const stockFilterOptions = [
    {
        value: "all",
        label: "All",
    },
    {
        value: "available",
        label: "Items With Stock Available",
    },
    {
        value: "belowMin",
        label: "Items Below Min Level",
    },
];

const StockRegister = () => {
    const [selectedFactory, setSelectedFactory] = useState(
        factoryOptions.find(
            (factory) => factory.value === DEFAULT_FACTORY_ID
        )
    );

    const [selectedCategory, setSelectedCategory] = useState(
        allCategoryOption
    );

    const [selectedStockFilter, setSelectedStockFilter] = useState(
        stockFilterOptions[0]
    );

    const [categoryOptions, setCategoryOptions] = useState([
        allCategoryOption,
    ]);

    const [loadingCategories, setLoadingCategories] = useState(false);
    const [stockData, setStockData] = useState([]);
    const [loading, setLoading] = useState(false);

    // Fetch categories
    const fetchItemCategories = async () => {
        setLoadingCategories(true);

        try {
            const res = await api.get(
                "/purchase-item-category/get-name"
            );

            const data = res.data || [];

            const options = data.map((category) => ({
                value: category._id,
                label: category.itemcat_title,
            }));

            setCategoryOptions([
                allCategoryOption,
                ...options,
            ]);
        } catch (error) {
            console.error(
                "Error fetching item categories:",
                error
            );

            setCategoryOptions([allCategoryOption]);
        } finally {
            setLoadingCategories(false);
        }
    };

    // Fetch stock register
    const fetchStockRegister = async () => {
        try {
            setLoading(true);

            const response = await api.get(
                "/inventory/stockRegister",
                {
                    params: {
                        factory:
                            selectedFactory?.value ||
                            DEFAULT_FACTORY_ID,

                        category:
                            selectedCategory?.value || "",
                    },
                }
            );
            setStockData(response.data.data || []);
        } catch (error) {
            console.error(
                "Stock register API error:",
                error
            );

            setStockData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchItemCategories();
    }, []);

    useEffect(() => {
        fetchStockRegister();
    }, [selectedFactory, selectedCategory]);

    // Factory change
    const handleFactoryChange = (option) => {
        setSelectedFactory(option);
        setSelectedCategory(allCategoryOption);
    };

    // Filter stock records
    const filteredStockData = stockData.filter((item) => {
        const stockQty = Number(item.stock_qty || 0);
        const minLevel = Number(item.stock_min_level || 0);

        switch (selectedStockFilter?.value) {
            case "available":
                return stockQty > 0;

            case "belowMin":
                return (
                    item.stock_min_level != null &&
                    item.stock_min_level !== "" &&
                    minLevel > 0 &&
                    stockQty < minLevel
                );

            case "all":
            default:
                return true;
        }
    });

    // Check whether stock is below the minimum level
    const isBelowMinimum = (row) => {
        const stockQty = Number(row.stock_qty || 0);
        const minLevel = Number(row.stock_min_level || 0);

        return (
            row.stock_min_level != null &&
            row.stock_min_level !== "" &&
            minLevel > 0 &&
            stockQty < minLevel
        );
    };

    // Material React Table columns
    const columns = [
        {
            id: "serialNumber",
            header: "S.No.",

            Cell: ({ row }) => row.index + 1,

            enableSorting: false,
            enableColumnFilter: false,
            size: 80,
        },
        {
            accessorFn: (row) =>
                row.item_title?.trim() || "-",

            id: "item_title",
            header: "Item Name",

            size: 220,
        },
        {
            accessorFn: (row) =>
                row.categoryName || "-",

            id: "categoryName",
            header: "Category",

            size: 180,
        },
        {
            accessorFn: (row) =>
                row.factoryName || "-",

            id: "factoryName",
            header: "Factory",

            size: 180,
        },
        {
            accessorFn: (row) =>
                Number(row.stock_qty || 0),

            id: "stock_qty",
            header: "Stock Quantity",

            Cell: ({ row }) => (
                <div className="flex items-center justify-end gap-1 whitespace-nowrap">
                    <span>
                        {Number(
                            row.original.stock_qty || 0
                        ).toLocaleString("en-IN", {
                            maximumFractionDigits: 3,
                        })}
                    </span>

                    <span className="text-sm text-purple-600">
                        {row.original.item_unit || ""}
                    </span>
                </div>
            ),

            sortingFn: "basic",
            enableColumnFilter: false,
            size: 160,

            muiTableHeadCellProps: {
                align: "right",
            },

            muiTableBodyCellProps: {
                align: "right",
            },
        },
        {
            accessorFn: (row) =>
                Number(row.stock_value || 0),

            id: "stock_value",
            header: "Stock Value",

            Cell: ({ cell }) =>
                `₹ ${Number(
                    cell.getValue() || 0
                ).toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                })}`,

            sortingFn: "basic",
            enableColumnFilter: false,
            size: 170,

            muiTableHeadCellProps: {
                align: "right",
            },

            muiTableBodyCellProps: {
                align: "right",
            },
        },
        {
            accessorFn: (row) =>
                row.stock_min_level ?? "-",

            id: "stock_min_level",
            header: "Min Level Qty",

            Cell: ({ row }) => {
                const minLevel =
                    row.original.stock_min_level;

                if (minLevel == null || minLevel === "") {
                    return "-";
                }

                return Number(minLevel).toLocaleString(
                    "en-IN",
                    {
                        maximumFractionDigits: 3,
                    }
                );
            },

            size: 150,
        },
    ];

    // Summary totals (preserving existing logic)
    const totalStockQty = stockData.reduce(
        (total, item) =>
            total + Number(item.stock_qty || 0),
        0
    );

    const totalStockValue = stockData.reduce(
        (total, item) =>
            total + Number(item.stock_value || 0),
        0
    );

    // Material React Table configuration
    const table = MaterialReactTable;
 const exportToExcel = (data) => {
    if (!data || data.length === 0) {
        alert("No data available to export");
        return;
    }

    const excelData = data.map((item, index) => ({
        "S.No.": index + 1,
        "Factory Name": item.factoryName || "-",
        "Category Name": item.categoryName || "-",
        "Item Name": item.item_title || "-",
        "Unit": item.item_unit || "-",
        "Stock Quantity": Number(item.stock_qty || 0),
        "Stock Value": Number(item.stock_value || 0),
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);

    worksheet["!cols"] = [
        { wch: 8 },
        { wch: 25 },
        { wch: 22 },
        { wch: 25 },
        { wch: 12 },
        { wch: 18 },
        { wch: 18 },
    ];

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        "Stock Register"
    );

    XLSX.writeFile(
        workbook,
        `Stock_Register_${new Date().toISOString().slice(0, 10)}.xlsx`
    );
};
    return (
        <div className="min-h-screen bg-gray-50 p-5">
            <h1 className="mb-5 text-2xl font-bold text-gray-800">
                Stock Register
            </h1>

            {/* Filters */}
            <div className="mb-5 grid w-full grid-cols-1 gap-4 rounded-xl bg-white p-4 shadow-sm md:w-1/2 md:grid-cols-3">
                {/* Factory Selector */}
                <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                        Select Factory
                    </label>

                    <Select
                        options={factoryOptions}
                        value={selectedFactory}
                        onChange={handleFactoryChange}
                        placeholder="Select factory"
                        isClearable={false}
                    />
                </div>

                {/* Category Selector */}
                <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                        Select Category
                    </label>

                    <Select
                        options={categoryOptions}
                        value={selectedCategory}
                        onChange={(option) =>
                            setSelectedCategory(option || allCategoryOption)
                        }
                        placeholder="Select category"
                        isClearable={false}
                        isLoading={loadingCategories}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={{
                            menuPortal: (base) => ({
                                ...base,
                                zIndex: 9999,
                            }),
                        }}
                    />
                </div>

                {/* Stock Filter */}
                <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                        Stock Filter
                    </label>

                    <Select
                        options={stockFilterOptions}
                        value={selectedStockFilter}
                        onChange={(option) =>
                            setSelectedStockFilter(
                                option || stockFilterOptions[0]
                            )
                        }
                        placeholder="Select stock filter"
                        isClearable={false}
                    />
                </div>
            </div>

            {/* Summary Cards */}
            <div className="mb-5 grid w-full grid-cols-1 gap-3 sm:w-1/2 sm:grid-cols-2">
                <div className="rounded-xl bg-purple-100 p-4 shadow-sm">
                    <p className="text-center text-sm text-gray-500">
                        Total Stock Quantity
                    </p>

                    <h2 className="mt-2 text-center text-xl font-bold text-gray-800">
                        {totalStockQty.toLocaleString("en-IN", {
                            maximumFractionDigits: 3,
                        })}
                    </h2>
                </div>

                <div className="rounded-xl bg-cyan-100 p-4 shadow-sm">
                    <p className="text-center text-sm text-gray-500">
                        Total Stock Value
                    </p>

                    <h2 className="mt-2 text-center text-xl font-bold text-gray-800">
                        ₹{" "}
                        {totalStockValue.toLocaleString("en-IN", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                        })}
                    </h2>
                </div>
            </div>
                        <button
                    onClick={() => exportToExcel(stockData)}
                    className="inline-flex items-center mb-2 gap-2 bg-green-600 hover:bg-green-700 active:scale-95 text-white px-2 py-2.5 rounded-lg font-medium text-sm shadow-sm hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                >
                    <FileUp size={18} strokeWidth={2} />
                    Export
                </button>
            {/* Material React Table */}
            <div className="rounded-xl bg-white p-4 shadow-sm">
                {loading ? (
                    <Loader />
                ) : (
                    <MaterialReactTable
                        columns={columns}
                        data={filteredStockData}
                        enablePagination
                        enableSorting
                        enableColumnFilters={false}
                        enableGlobalFilter
                        enableDensityToggle={false}
                        enableFullScreenToggle
                        enableHiding
                        enableStickyHeader
                        initialState={{
                            pagination: {
                                pageIndex: 0,
                                pageSize: 10,
                            },
                            density: "comfortable",
                        }}
                        muiPaginationProps={{
                            rowsPerPageOptions: [
                                10,
                                25,
                                50,
                                100,
                            ],
                            showFirstButton: true,
                            showLastButton: true,
                        }}
                        muiTableContainerProps={{
                            sx: {
                                maxHeight: "600px",
                            },
                        }}
                        muiTablePaperProps={{
                            elevation: 0,
                            sx: {
                                borderRadius: "8px",
                                overflow: "hidden",
                            },
                        }}
                        muiTableHeadCellProps={{
                            sx: {
                                backgroundColor: "#f9fafb",
                                color: "#4b5563",
                                fontSize: "12px",
                                fontWeight: 600,
                                borderBottom:
                                    "1px solid #e5e7eb",
                                whiteSpace: "nowrap",
                            },
                        }}
                        muiTableBodyCellProps={{
                            sx: {
                                fontSize: "13px",
                                color: "#374151",
                                py: 1.5,
                            },
                        }}
                        muiTableBodyRowProps={({ row }) => ({
                            sx: {
                                backgroundColor: isBelowMinimum(
                                    row.original
                                )
                                    ? "#fee2e2"
                                    : "inherit",

                                color: isBelowMinimum(
                                    row.original
                                )
                                    ? "#b91c1c"
                                    : "inherit",

                                "&:hover td": {
                                    backgroundColor: isBelowMinimum(
                                        row.original
                                    )
                                        ? "#fecaca"
                                        : "#f9fafb",
                                },
                            },
                        })}
                        renderEmptyRowsFallback={() => (
                            <div className="w-full py-8 text-center text-sm text-gray-500">
                                No stock records found.
                            </div>
                        )}
                    />
                )}
            </div>
        </div>
    );
};

export default StockRegister;